import React, { useState, useEffect } from "react";
import useResponsive from "@/hooks/useResponsive";
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Popconfirm,
  Card,
  Typography,
  Row,
  Col,
  Upload,
  Image,
  Progress,
  Switch,
} from "antd";

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FireOutlined,
  SearchOutlined,
  UploadOutlined,
  EyeOutlined,
  PictureOutlined,
} from "@ant-design/icons";

import apiClient from "../utils/apiClient";

import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const HotOffersManagement = () => {
const { isMobile } = useResponsive();

  // =====================================================
  // STATE
  // =====================================================

  const [offers, setOffers] = useState([]);

  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);

  const [editingOffer, setEditingOffer] = useState(null);

  const [search, setSearch] = useState("");

  const [imageUrl, setImageUrl] = useState("");

  const [uploading, setUploading] = useState(false);

  const [form] = Form.useForm();

  // =====================================================
  // FETCH OFFERS
  // =====================================================

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);

      const res = await apiClient.get("/offers");

      setOffers(res?.data?.data || res?.data || []);
    } catch (err) {
      console.error("Fetch offers error:", err);

      message.error("Failed to fetch offers");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // RESET MODAL
  // =====================================================

  const resetModalState = () => {
    setModalVisible(false);

    setEditingOffer(null);

    setImageUrl("");

    form.resetFields();
  };

  // =====================================================
  // ADD OFFER
  // =====================================================

  const handleAdd = () => {
    setEditingOffer(null);

    setImageUrl("");

    form.resetFields();

    setModalVisible(true);
  };

  // =====================================================
  // EDIT OFFER
  // =====================================================

  const handleEdit = (offer) => {
    setEditingOffer(offer);

    setImageUrl(offer.image || "");

    form.setFieldsValue({
      status: offer.status,
      isFeatured: offer.isFeatured,

      title: offer.title,

      description: offer.description,

      promoCode: offer.offerCode,

      discountType: offer.discountType,

      discountValue: offer.discountValue,

      minBookingValue: offer.minAmount,

      maxBookingValue: offer.maxAmount,

      maxUsageCount: offer.maxUsageCount,

offerLockDurationMinutes:
  offer.offerLockDurationMinutes || 60,

      dateRange:
        offer.validFrom && offer.validUntil
          ? [dayjs(offer.validFrom), dayjs(offer.validUntil)]
          : [],
    });

    setModalVisible(true);
  };

  // =====================================================
  // DELETE OFFER
  // =====================================================

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/offers/${id}`);

      message.success("Offer deleted successfully");

      fetchOffers();
    } catch (err) {
      console.error("Delete offer error:", err);

      message.error(err?.response?.data?.message || "Delete failed");
    }
  };

  // =====================================================
  // SUBMIT
  // =====================================================

  const handleSubmit = async (values) => {
    try {
      const payload = {
        image: imageUrl,

        status: values.status || "draft",

        isFeatured: values.isFeatured || false,

        title: values.title,

        description: values.description,

        offerCode: values.promoCode,

        discountType: values.discountType,

        discountValue: values.discountValue,

        minAmount: values.minBookingValue,

        maxAmount: values.maxBookingValue,

        maxUsageCount: values.maxUsageCount,

offerLockDurationMinutes:
  values.offerLockDurationMinutes || 60,

        validFrom: values.dateRange?.[0]?.toISOString(),

        validUntil: values.dateRange?.[1]?.toISOString(),
      };

      if (editingOffer) {
        await apiClient.put(`/offers/${editingOffer._id}`, payload);

        message.success("Offer updated successfully");
      } else {
        await apiClient.post("/offers", payload);

        message.success("Offer created successfully");
      }

      resetModalState();

      fetchOffers();
    } catch (err) {
      console.error("Save offer error:", err);

      message.error(err?.response?.data?.message || "Error saving offer");
    }
  };

  // =====================================================
  // IMAGE UPLOAD
  // =====================================================

  const handleImageUpload = async (file) => {
    try {
      const isImage = file.type.startsWith("image/");

      if (!isImage) {
        message.error("Only image files allowed");

        return Upload.LIST_IGNORE;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isLt5M) {
        message.error("Image must be under 5MB");

        return Upload.LIST_IGNORE;
      }

      setUploading(true);

      const formData = new FormData();

      formData.append("image", file);

      const res = await apiClient.post("/offers/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedImage = res?.data?.data?.imageUrl;

      if (!uploadedImage) {
        throw new Error("Upload failed");
      }

      setImageUrl(uploadedImage);

      message.success("Image uploaded successfully");

      return false;
    } catch (error) {
      console.error("Offer upload error:", error);

      message.error(error?.response?.data?.message || "Upload failed");

      return Upload.LIST_IGNORE;
    } finally {
      setUploading(false);
    }
  };

  // =====================================================
  // FILTERED OFFERS
  // =====================================================

  const filteredOffers = Array.isArray(offers)
    ? offers.filter((offer) =>
        offer?.title?.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  // =====================================================
  // TABLE COLUMNS
  // =====================================================

  const columns = [
    {
      title: "Banner",

      dataIndex: "image",

      width: 120,

      render: (image) =>
        image ? (
          <Image
            src={
              image?.startsWith("http")
                ? image
                : `${window.location.origin}${image}`
            }
            width={90}
            height={60}
            style={{
              objectFit: "cover",
              borderRadius: 8,
            }}
            preview={{
              mask: <EyeOutlined />,
            }}
          />
        ) : (
          <div
            style={{
              width: 90,
              height: 60,
              background: "#f5f5f5",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PictureOutlined />
          </div>
        ),
    },

    {
      title: "Offer",

      dataIndex: "title",

      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>

          <br />

          <Text
            type="secondary"
            style={{
              fontSize: 12,
            }}
          >
            {record.description}
          </Text>
        </div>
      ),
    },

    {
      title: "Promo Code",

      render: (_, r) => <Tag color="blue">{r.offerCode}</Tag>,
    },

    {
      title: "Discount",

      render: (_, r) => (
        <Tag color="red">
          {r.discountType === "percentage"
            ? `${r.discountValue}% OFF`
            : `₹${r.discountValue} OFF`}
        </Tag>
      ),
    },

    {
      title: "Range",

      render: (_, r) => (
        <span>
          ₹{r.minAmount || 0}
          {" - "}₹{r.maxAmount || "∞"}
        </span>
      ),
    },

    {
      title: "Usage",

      render: (_, r) => {
        const percent = r.maxUsageCount
          ? Math.round(((r.currentUsageCount || 0) / r.maxUsageCount) * 100)
          : 0;

        return (
          <div
            style={{
              minWidth: 120,
            }}
          >
            <Text>
              {r.currentUsageCount || 0}/{r.maxUsageCount || "∞"}
            </Text>

            <Progress percent={percent} size="small" showInfo={false} />
          </div>
        );
      },
    },

    {
      title: "Status",

      render: (_, record) => {
        const colorMap = {
          active: "green",
          paused: "orange",
          expired: "red",
          draft: "default",
        };

        return (
          <Tag color={colorMap[record.status] || "default"}>
            {(record.status || "draft").toUpperCase()}
          </Tag>
        );
      },
    },

    {
      title: "Validity",

      render: (_, r) => (
        <div>
          <div>{dayjs(r.validFrom).format("DD MMM YYYY")}</div>

          <Text type="secondary">to</Text>

          <div>{dayjs(r.validUntil).format("DD MMM YYYY")}</div>
        </div>
      ),
    },

    {
      title: "Featured",

      render: (_, record) =>
        record.isFeatured ? <Tag color="gold">FEATURED</Tag> : "-",
    },

    {
      title: "Actions",

      width: 140,

      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />

          <Popconfirm
            title="Delete offer?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // =====================================================
  // UI
  // =====================================================

  return (
    <div
      style={{
        padding: 24,
      }}
    >
      <Card
        style={{
          borderRadius: 20,
        }}
      >
        {/* =====================================================
            HEADER
        ===================================================== */}

<Row
  justify="space-between"
  align={isMobile ? "stretch" : "middle"}
  gutter={[16,16]}
  style={{
    marginBottom:20,
  }}
>

          <Col xs={24} md={16}>
            <Title
              level={2}
              style={{
                margin: 0,
              }}
            >
              <FireOutlined
                style={{
                  color: "#ff4d4f",
                  marginRight: 10,
                }}
              />
              Hot Offers
            </Title>

            <Text type="secondary">
              Manage promotional offers and discounts
            </Text>
          </Col>

          <Col xs={24} md={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              size="large"
            >
              Create Offer
            </Button>
          </Col>
        </Row>

        {/* =====================================================
            SEARCH
        ===================================================== */}

        <Row
          style={{
            marginBottom: 20,
          }}
        >
          <Col span={8}>
            <Input
              placeholder="Search offers..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
        </Row>

        {/* =====================================================
            TABLE
        ===================================================== */}

        <Table
          columns={columns}
          dataSource={filteredOffers}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* =====================================================
          MODAL
      ===================================================== */}

      <Modal
        title={editingOffer ? "Edit Offer" : "Create Offer"}
        open={modalVisible}
        footer={null}
        onCancel={resetModalState}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{
            marginTop: 20,
          }}
        >
          {/* TITLE */}

          <Form.Item
            name="title"
            label="Offer Title"
            rules={[
              {
                required: true,
                message: "Please enter offer title",
              },
            ]}
          >
            <Input placeholder="Enter offer title" />
          </Form.Item>

          {/* DESCRIPTION */}

          <Form.Item
            name="description"
            label="Description"
            rules={[
              {
                required: true,
                message: "Please enter offer description",
              },
            ]}
          >
            <TextArea rows={4} placeholder="Enter offer description" />
          </Form.Item>

          {/* OFFER IMAGE */}

          <Form.Item label="Offer Banner">
            <Upload
              name="image"
              listType="picture-card"
              showUploadList={false}
              beforeUpload={handleImageUpload}
              accept="image/*"
            >
              {imageUrl ? (
                <Image
                  src={
                    imageUrl?.startsWith("http")
                      ? imageUrl
                      : `${window.location.origin}${imageUrl}`
                  }
                  preview={false}
                  style={{
                    width: 120,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              ) : (
                <div>
                  {uploading ? (
                    "Uploading..."
                  ) : (
                    <>
                      <UploadOutlined />

                      <div
                        style={{
                          marginTop: 8,
                        }}
                      >
                        Upload
                      </div>
                    </>
                  )}
                </div>
              )}
            </Upload>
          </Form.Item>

          {/* PROMO CODE */}

          <Form.Item
            name="promoCode"
            label="Promo Code"
            rules={[
              {
                required: true,
                message: "Please enter promo code",
              },
            ]}
          >
            <Input placeholder="SUMMER50" />
          </Form.Item>

          {/* DISCOUNT TYPE */}

          <Form.Item
            name="discountType"
            label="Discount Type"
            rules={[
              {
                required: true,
                message: "Please select discount type",
              },
            ]}
          >
            <Select>
              <Option value="percentage">Percentage</Option>

              <Option value="fixed_amount">Fixed Amount</Option>
            </Select>
          </Form.Item>

          {/* DISCOUNT VALUE */}

          <Form.Item
            name="discountValue"
            label="Discount Value"
            rules={[
              {
                required: true,
                message: "Please enter discount value",
              },
            ]}
          >
            <InputNumber
              style={{
                width: "100%",
              }}
            />
          </Form.Item>

          {/* MIN / MAX */}

          <div
            style={{
              display: "flex",
              gap: 12,
            }}
          >
            <Form.Item
              name="minBookingValue"
              label="Min Booking Value"
              style={{
                flex: 1,
              }}
            >
              <InputNumber
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>

            <Form.Item
              name="maxBookingValue"
              label="Max Booking Value"
              style={{
                flex: 1,
              }}
            >
              <InputNumber
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>
          </div>

          {/* MAX USAGE */}

          <Form.Item
            name="maxUsageCount"
            label="Max Usage Count"
            rules={[
              {
                required: true,
                message: "Please enter max usage count",
              },
            ]}
          >
            <InputNumber
              style={{
                width: "100%",
              }}
            />
          </Form.Item>

{/* OFFER LOCK TIMER */}

<Form.Item
  name="offerLockDurationMinutes"
  label="Offer Lock Duration (Minutes)"
  initialValue={60}
  extra="How long discounted pricing remains locked after quotation generation"
>
  <InputNumber
    min={1}
    max={1440}
    style={{
      width: "100%",
    }}
    addonAfter="mins"
  />
</Form.Item>

          {/* DATE RANGE */}

          <Form.Item
            name="dateRange"
            label="Offer Validity"
            rules={[
              {
                required: true,
                message: "Please select validity dates",
              },
            ]}
          >
            <RangePicker
              style={{
                width: "100%",
              }}
            />
          </Form.Item>

          {/* STATUS */}

          <Form.Item name="status" label="Offer Status" initialValue="draft">
            <Select>
              <Option value="draft">Draft</Option>

              <Option value="active">Active</Option>

              <Option value="paused">Paused</Option>
            </Select>
          </Form.Item>

          {/* FEATURED */}

          <Form.Item
            name="isFeatured"
            label="Featured Offer"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {/* FOOTER */}

          <Form.Item
            style={{
              marginTop: 30,
              marginBottom: 0,
            }}
          >
            <Space>
              <Button type="primary" htmlType="submit">
                {editingOffer ? "Update Offer" : "Create Offer"}
              </Button>

              <Button onClick={resetModalState}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HotOffersManagement;
